import type {
  SearchFilterSelection,
  SearchFilterType,
  SearchPagination,
  SearchSorting,
} from '@/scopes/search/search/infrastructure/api/types';
import { createStore, type StoreApi } from 'zustand';

import { DEFAULT_RESULTS_PER_PAGE, DEFAULT_SORTING_BY } from '@/scopes/search/search/domain/constants';

export interface SearchState {
  pagination: SearchPagination;
  sorting: SearchSorting;
  filterSelection: SearchFilterSelection;
}

export interface SearchStoreActions {
  setPage: (pageNumber: number | string) => void;
  setNextPage: () => void;
  setPreviousPage: () => void;
  setSortingBy: (by: string) => void;
  setFilterSelection: <K extends SearchFilterType>(filterType: K, value: SearchFilterSelection[K]) => void;
  resetFilterValues: <K extends SearchFilterType>(filterTypesToReset: K[]) => void;
}

export type SearchStore = SearchState & SearchStoreActions;

const initialState = {
  pagination: {
    offset: 0,
    limit: DEFAULT_RESULTS_PER_PAGE,
  },
  sorting: {
    by: DEFAULT_SORTING_BY,
  },
  filterSelection: {},
};

export type SearchStoreApi = Pick<StoreApi<SearchStore>, 'getState'>;

export const createSearchStore = (initState: SearchState) =>
  createStore<SearchStore>((set) => ({
    ...initState,
    setNextPage: () => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: pagination.offset + DEFAULT_RESULTS_PER_PAGE,
        },
      }));
    },
    setPreviousPage: () => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: Math.max(0, pagination.offset - DEFAULT_RESULTS_PER_PAGE),
        },
      }));
    },
    setPage: (pageNumber) => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: Math.max(0, Number(pageNumber) * pagination.limit - DEFAULT_RESULTS_PER_PAGE),
        },
      }));
    },
    setSortingBy: (by) => {
      set(({ pagination }) => ({
        pagination: {
          ...pagination,
          offset: 0,
        },
        sorting: { by },
      }));
    },
    setFilterSelection: (filterType, value) => {
      set(({ filterSelection, pagination }) => ({
        pagination: {
          ...pagination,
          offset: 0,
        },
        filterSelection: {
          ...filterSelection,
          [filterType]: value,
        },
      }));
    },
    resetFilterValues: <K extends SearchFilterType>(filterTypesToReset: K[]) => {
      set(({ filterSelection, pagination }) => ({
        pagination: {
          ...pagination,
          offset: 0,
        },
        filterSelection: Object.entries(filterSelection).reduce((acc, [filterType, filterValue]) => {
          if (filterTypesToReset.includes(filterType as K)) return acc;

          return {
            ...acc,
            [filterType]: filterValue,
          };
        }, {}),
      }));
    },
  }));

export const searchStore = createSearchStore(initialState);
